import { api } from "@/lib/api";
import type { Page } from "@/features/users/api";

export type AmcStatus = "none" | "active" | "expiring_soon" | "expired";

export type ServiceContractType =
  | "amc"
  | "one_year"
  | "three_years"
  | "five_years_after_sales"
  | "paid_service"
  | "other";

export type ServiceStatus = "active" | "expired" | "na";

export const SERVICE_CONTRACT_TYPE_LABELS: Record<ServiceContractType, string> = {
  amc: "AMC",
  one_year: "1 Year",
  three_years: "3 Years",
  five_years_after_sales: "5 Years After-Sales Service",
  paid_service: "Paid Service",
  other: "Other",
};

export const SERVICE_STATUS_LABELS: Record<ServiceStatus, string> = {
  active: "Active",
  expired: "Expired",
  na: "N/A",
};

export interface CustomerListItem {
  id: string;
  code: string;
  company_name: string;
  site_id: string | null;
  site_name: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  gst_number: string | null;
  account_manager_id: string | null;
  account_manager_name: string | null;
  amc_expiry_date: string | null;
  amc_status: AmcStatus;
  days_to_amc_expiry: number | null;
  service_contract_type: ServiceContractType | null;
  service_contract_start_date: string | null;
  service_contract_expiry_date: string | null;
  service_status: ServiceStatus | null;
  location_count: number;
  status: string;
}

export interface CustomerLocation {
  id: string;
  customer_id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  contact_name: string | null;
  contact_phone: string | null;
  is_primary: boolean;
}

export interface CustomerContact {
  id: string;
  customer_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  designation: string | null;
  is_primary: boolean;
}

export interface CustomerDetail extends CustomerListItem {
  address_line1: string | null;
  address_line2: string | null;
  state: string | null;
  pincode: string | null;
  contact_name: string | null;
  notes: string | null;
  locations: CustomerLocation[];
  contacts: CustomerContact[];
  created_at: string;
}

export interface CustomerFilters {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  account_manager_id?: string;
  amc_expiring_days?: number;
  site_id?: string;
}

export interface CustomerBody {
  company_name: string;
  site_id?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  gst_number?: string | null;
  account_manager_id?: string | null;
  amc_expiry_date?: string | null;
  service_contract_type?: ServiceContractType | null;
  service_contract_start_date?: string | null;
  service_contract_expiry_date?: string | null;
  service_status?: ServiceStatus | null;
  notes?: string | null;
}

function qs(filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function listCustomers(filters: CustomerFilters = {}): Promise<Page<CustomerListItem>> {
  return api.get<Page<CustomerListItem>>(`/customers${qs(filters)}`);
}

export function getCustomer(id: string): Promise<CustomerDetail> {
  return api.get<CustomerDetail>(`/customers/${id}`);
}

export function createCustomer(body: CustomerBody): Promise<CustomerDetail> {
  return api.post<CustomerDetail>("/customers", body);
}

export function updateCustomer(id: string, body: Partial<CustomerBody>): Promise<CustomerDetail> {
  return api.patch<CustomerDetail>(`/customers/${id}`, body);
}

export function deleteCustomer(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/customers/${id}`);
}

export function addLocation(
  customerId: string,
  body: { name: string; city?: string | null; address?: string | null; is_primary?: boolean },
): Promise<CustomerLocation> {
  return api.post<CustomerLocation>(`/customers/${customerId}/locations`, body);
}

export function updateLocation(
  id: string,
  body: Partial<CustomerLocation>,
): Promise<CustomerLocation> {
  return api.patch<CustomerLocation>(`/locations/${id}`, body);
}

export function deleteLocation(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/locations/${id}`);
}

export function addContact(
  customerId: string,
  body: { name: string; email?: string | null; phone?: string | null; designation?: string | null },
): Promise<CustomerContact> {
  return api.post<CustomerContact>(`/customers/${customerId}/contacts`, body);
}

export function deleteContact(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/contacts/${id}`);
}

// --- portal users (customer logins) ---

export interface PortalUser {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  is_active: boolean;
  can_view_all_tickets: boolean;
  last_login_at: string | null;
  location_id: string | null;
  location_name: string | null;
}

export interface PortalUserCreated {
  user: PortalUser;
  temporary_password: string;
}

export function listPortalUsers(customerId: string): Promise<PortalUser[]> {
  return api.get<PortalUser[]>(`/customers/${customerId}/portal-users`);
}

export function createPortalUser(
  customerId: string,
  body: {
    full_name: string;
    email: string;
    phone?: string | null;
    can_view_all_tickets?: boolean;
    location_id?: string | null;
  },
): Promise<PortalUserCreated> {
  return api.post<PortalUserCreated>(`/customers/${customerId}/portal-users`, body);
}

export function updatePortalUser(
  customerId: string,
  userId: string,
  body: { is_active?: boolean; can_view_all_tickets?: boolean; location_id?: string | null },
): Promise<PortalUser> {
  return api.patch<PortalUser>(`/customers/${customerId}/portal-users/${userId}`, body);
}

export function resetPortalUserPassword(
  customerId: string,
  userId: string,
): Promise<{ temporary_password: string; message: string }> {
  return api.post<{ temporary_password: string; message: string }>(
    `/customers/${customerId}/portal-users/${userId}/reset-password`,
  );
}

export function deletePortalUser(customerId: string, userId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/customers/${customerId}/portal-users/${userId}`);
}
